// src/services/queries/macro/economicCalendarQueries.ts

import type { QueryFunction } from "@tanstack/react-query";
import { macroQueryKeys } from "@/constants/queryKeys";
import { TIME } from "@/constants/app";
import { API_BASE_URL } from "@/services";
import type { EconomicCalendarEvent, EconomicCalendarResponse } from "@/types/dashboards/macro";

export interface EconomicCalendarQueryParams {
  startDate?: string; // YYYY-MM-DD format
  endDate?: string; // YYYY-MM-DD format
}

/**
 * Fetch economic calendar events from backend API
 */
export const fetchEconomicCalendarEvents: QueryFunction<EconomicCalendarEvent[]> = async ({
  queryKey,
  signal,
}) => {
  const [, , params] = queryKey;
  if (!params || typeof params !== "object") {
    throw new Error("Invalid query parameters");
  }

  const { startDate, endDate } = params as EconomicCalendarQueryParams;

  const requestInit: RequestInit = {};
  if (signal) {
    requestInit.signal = signal;
  }

  // Build query parameters
  const queryParams = new URLSearchParams();
  if (startDate) {
    queryParams.append("start_date", startDate);
  }
  if (endDate) {
    queryParams.append("end_date", endDate);
  }

  const url = `${API_BASE_URL}/macroIndicators/calendar${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
  
  const response = await fetch(url, requestInit);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  const data = (await response.json()) as EconomicCalendarResponse;
  return data.data;
};

// Query options factory
export const economicCalendarQueryOptions = (params: EconomicCalendarQueryParams = {}) => ({
  queryKey: macroQueryKeys.calendar(params),
  queryFn: fetchEconomicCalendarEvents,
  staleTime: 5 * TIME.MINUTE, // 5 minutes
  gcTime: 10 * TIME.MINUTE, // 10 minutes
  retry: 2,
  retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
});
